import {
  CalendarConfigurationDto,
  CalendarConfigurationListDto,
  QueryFactory,
} from '@lmt/shared-types';

import styles from '@lmt/unicorn-shared/app/components/calendar/configuration/list/CalendarConfigurationList.module.scss';

import { Button } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import PinIcon from '@lmt/unicorn-shared/app/icons/pin.svg?react';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import React, { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

interface Props {
  currentConfigurationId?: number;
  onConfigurationChange: (newConfigurationId: number) => void;
  configurations?: CalendarConfigurationListDto[];
}

export const CalendarConfigurationList: React.FC<Props> = React.memo(
  function CalendarConfigurationList(props) {
    const claims = useClaims();
    const i18n = useScopedTranslation('calendar_configuration');
    const i18nGlobal = useTranslation();
    const forms = useGlobalForms();
    const dispatch = useUniDispatch();
    const modals = useModal();
    const queryClient = useQueryClient();
    const { onConfigurationChange, configurations, currentConfigurationId } =
      props;

    // this is used to close the popup and reset
    const [key, setKey] = useState(1);
    const closeDropdown = useCallback(() => {
      setKey((key) => ++key);
    }, []);

    const pinConfiguration = useCallback(
      async (configurationId: number, isPinned: boolean) => {
        try {
          await QueryFactory.CalendarConfigurationClient.pinCalendarConfiguration(
            configurationId,
            { isPinned: isPinned },
          );
          await queryClient.invalidateQueries({
            queryKey:
              QueryFactory.CalendarConfigurationQuery.getCalendarConfigurationsQueryKey(),
          });
        } catch (e) {
          await modals.showError({ text: convertToErrorString(e) });
        }
      },
      [],
    );

    const onConfigurationCreatingOrEditing = useCallback(
      async (id?: number) => {
        closeDropdown();
        let configuration: CalendarConfigurationDto | undefined;
        if (id) {
          configuration = await forms.openCalendarConfigurationEditing(id);
        } else {
          configuration = await forms.openCalendarConfigurationCreating();
        }
        if (configuration) {
          onConfigurationChange(configuration.id);
        }
      },
      [closeDropdown, onConfigurationChange],
    );

    const renderConfigurationOption = useCallback(
      (config: CalendarConfigurationListDto) => {
        const canEdit =
          claims.permissions.manageSharedCalendarConfigurations ||
          claims.id === config.userId;
        return (
          <div className={styles.controls}>
            {canEdit && (
              <a
                className={styles.editLink}
                onClick={(e) => {
                  e.stopPropagation(); // to prevent onOptionClick
                  onConfigurationCreatingOrEditing(config.id);
                }}
              >
                {i18n.t('list.edit_button')}
              </a>
            )}
            <PinIcon
              className={styles.pin}
              data-is-pinned={config.isPinned}
              onClick={async (event) => {
                event.stopPropagation();
                await pinConfiguration(config.id, !config.isPinned);
              }}
            />
          </div>
        );
      },
      [
        pinConfiguration,
        claims?.permissions?.manageSharedCalendarConfigurations,
      ],
    );

    const renderDropdownBottom = useMemo(() => {
      return (
        <div
          className={styles.bottomContainer}
          onMouseDown={(e) => e.preventDefault()}
          data-not-empty={!!configurations?.length}
        >
          <Button
            testId={'add-new-configuration'}
            className={styles.addButton}
            title={i18n.t('list.add_button')}
            onClick={() => {
              onConfigurationCreatingOrEditing();
            }}
          />
        </div>
      );
    }, []);

    const options = useMemo(() => {
      if (!configurations) {
        return [];
      }
      return configurations
        .map((configuration) => {
          const displayValue: string = i18nGlobal.t(configuration.name, {
            defaultValue: configuration.name,
          });
          return {
            ...configuration,
            displayValue: displayValue.toLowerCase(),
          };
        })
        .sort((a, b) => a.displayValue.localeCompare(b.displayValue));
    }, [configurations]);

    return (
      <DropDownInput
        key={key}
        options={options}
        classes={{
          option: styles.option,
        }}
        rootClassName={styles.container}
        onValueChanged={(option) => onConfigurationChange(option!.id)}
        idFunction={'id'}
        getOptionLabel={(item) =>
          i18nGlobal.t(item.name, { defaultValue: item.name })
        }
        required={true}
        postfixRenderer={renderConfigurationOption}
        useIdFunctionAsValue={true}
        value={currentConfigurationId}
        popupFooter={renderDropdownBottom}
        testId={'calendar-configuration-list'}
      />
    );
  },
);
